import type { NextPage } from 'next';
import styles from '../../styles/form/imageInput2.module.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { joinClassnames } from 'helpers/classNames';
import { faLock, faEnvelope, faUser } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { Size } from 'types/size';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import Resizer from 'react-image-file-resizer';
import { decode } from 'base64-arraybuffer';
import moment from 'moment';
import Script from 'next/script';
import { Capacitor } from '@capacitor/core';
import Break from 'componentsUnits/break';
import { CiImageOn } from 'react-icons/ci';
import { isDev } from 'app/general';

library.add(faLock, faEnvelope, faUser);

interface Props {
  setImageToUpload?: (image: File) => void;
  uploadImageImmediately?: (image: File) => boolean;
  text?: string;
  showImage?: boolean;
  setImageToShow?: (imageUrl: string) => void;
  size?: 'small' | 'auto_size' | 'xSmall' | 'xxSmall';
  break?: boolean;
  placeholderImage?: string;
  justIcon?: boolean;
}

// Helper function to resize image files
const resizeFile = (file: Blob): Promise<File> =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(file, 1920, 1080, 'JPEG', 70, 0, (uri) => {
      resolve(uri as File);
    }, 'file');
  });

const ImageInputComponent: NextPage<Props> = ({
  setImageToUpload,
  uploadImageImmediately,
  text = 'Choose Image',
  showImage,
  setImageToShow,
  size,
  break: showBreak,
  placeholderImage,
  justIcon,
}) => {
  const [image, setImage] = useState<string>('');

  const saveImage = async (img: Blob) => {
    try {
      const resizedImage: File = await resizeFile(img);
      const imageUrl = URL.createObjectURL(resizedImage);
      setImage(imageUrl);

      if (setImageToShow) setImageToShow(imageUrl);
      if (setImageToUpload) setImageToUpload(resizedImage);
      if (uploadImageImmediately) {
        uploadImageImmediately(resizedImage) || reset();
      }
    } catch (error) {
      isDev && console.error('Image resizing error', error);
    }
  };

  const takePicture = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: Capacitor.isNativePlatform() ? CameraSource.Prompt : CameraSource.Photos,
      });

      if (!image) {
        isDev && console.warn('No image taken');
        return;
      }

      const blob = new Blob([new Uint8Array(decode(image.base64String as string))], {
        type: `image/${image.format}`,
      });

      const file = new File([blob], `image_${moment().unix()}.jpg`, {
        lastModified: Date.now(),
        type: blob.type,
      });
      await saveImage(file);
    } catch (error) {
      isDev && console.error('Camera error', error);
    }
  };

  const reset = () => {
    setImage('');
  };

  const combinedClassNames = joinClassnames([
    styles.image_upload_input,
    size === 'small' && styles.small,
    size === 'auto_size' && styles.auto_size,
    size === 'xSmall' && styles.xSmall,
    size === 'xxSmall' && styles.xxSmall,
  ]);

  return (
    <div className={styles.image_upload_wrapper}>
      <Script type="module" src="https://unpkg.com/@ionic/pwa-elements@latest/dist/ionicpwaelements/ionicpwaelements.esm.js" />
      <Script noModule src="https://unpkg.com/@ionic/pwa-elements@latest/dist/ionicpwaelements/ionicpwaelements.js" />

      {showImage && (
        <div className={styles.image_wrapper}>
          <img className={styles.image} src={image || placeholderImage} alt="Selected" />
        </div>
      )}

      {showBreak && <Break size={Size.xSmall} />}

      <label htmlFor="image-upload">
        {justIcon ? (
          <div onClick={takePicture} className={styles.centerV}>
            <CiImageOn color='white' size={30} />
          </div>
        ) : (
          <div className={combinedClassNames} onClick={takePicture}>
            <div className={styles.text}>{text}</div>
          </div>
        )}
      </label>
    </div>
  );
};

export default ImageInputComponent;
